import React from "react";
import styled from "styled-components";
import { Copy } from "lucide-react";
import { BasicBotData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Modal from "common/Modal/Modal";
import Button from "common/Buttons/Button";
import { CopyToClipboardButton } from "common/CopyToClipboard/CopyToClipboard";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { getTvWebhookBaseUrl } from "app/shared/utils";
import TradingViewUUIDs from "features/bots/MyBots/TradingViewUUIDs";

interface Props {
  botData: BasicBotData;
  open: boolean;
  onClose: () => void;
}

/**
 * How to connect a TradingView alert to this bot: copy the webhook URL, copy
 * the bot's alert message, then create the alert in TradingView.
 */
const NewLookTvAlertSetupModal = ({ botData, open, onClose }: Props) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const webhookUrl = getTvWebhookBaseUrl();

  return (
    <Modal
      title={t("card.tv_setup.title")}
      open={open}
      onCancel={onClose}
      footer={
        <Footer>
          <Hint>{t("card.tv_setup.waiting")}</Hint>
          <Button type="primary" onClick={onClose}>
            {t("card.tv_setup.done")}
          </Button>
        </Footer>
      }
    >
      <Intro>{t("card.tv_setup.intro")}</Intro>
      <Steps>
        <Step>
          <Num>1</Num>
          <StepBody>
            <StepTitle>{t("card.tv_setup.step1")}</StepTitle>
            <UrlRow>
              <Url title={webhookUrl}>{webhookUrl}</Url>
              <CopyToClipboardButton
                name="Webhook URL"
                text={webhookUrl}
                icon={<Copy size={14} strokeWidth={1.75} />}
                shape={undefined}
              />
            </UrlRow>
          </StepBody>
        </Step>
        <Step>
          <Num>2</Num>
          <StepBody>
            <StepTitle>{t("card.tv_setup.step2")}</StepTitle>
            <MessageBox>
              <TradingViewUUIDs
                botData={botData}
                style={{ width: "100%", borderTop: "none", padding: 0 }}
              />
            </MessageBox>
          </StepBody>
        </Step>
        <Step>
          <Num>3</Num>
          <StepBody>
            <StepTitle>{t("card.tv_setup.step3_title")}</StepTitle>
            <StepText>{t("card.tv_setup.step3")}</StepText>
          </StepBody>
        </Step>
      </Steps>
    </Modal>
  );
};

export default NewLookTvAlertSetupModal;

const Intro = styled.p`
  margin: 0 0 16px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Steps = styled.ol`
  display: grid;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
`;
const Step = styled.li`
  display: flex;
  gap: 12px;
  min-width: 0;
`;
const Num = styled.span`
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
`;
const StepBody = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 8px;
`;
const StepTitle = styled.div`
  ${newLookType.panelTitle};
  font-size: 14px;
  line-height: 24px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const StepText = styled.div`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const UrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 6px 6px 6px 12px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Url = styled.code`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Hint = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: left;
`;
// The alert messages are long JSON: keep them inside the modal.
const MessageBox = styled.div`
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  * {
    min-width: 0;
    max-width: 100%;
  }
  span,
  p,
  div {
    overflow-wrap: anywhere;
    white-space: normal !important;
  }
`;
